/**
 * Zain Art Predictive Search Custom Element
 * Features:
 * - Web Component connectedCallback lifecycle
 * - 250ms Debounced input querying
 * - AbortController to cancel stale fetch requests
 * - Least-Recently-Used (LRU) cache (limit 30 entries)
 * - Accessibility (keyboard Arrow Up/Down, Enter, Escape, ARIA tags)
 * - Mobile screen overlay scroll-locking
 * - Dynamic Recent / Trending search generation & empty state loading
 * - Automatic dynamic creation & retrieval of dropdown container
 * - Emits custom 'predictive_search:selected' analytics events
 */

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.debounceTimer = null;
    this.abortController = null;
    this.cacheLimit = 30;
    this.cache = new Map(); // Simple LRU Cache
  }

  connectedCallback() {
    this.input = this.querySelector('input[type="text"], input[type="search"], input[name="q"]');
    this.form = this.querySelector('form');
    this.clearButton = this.querySelector('.hp-search-clear-btn');
    
    if (!this.input || !this.form) return;

    this.getDropdown();

    this.setupEventListeners();
    this.setupA11y();
  }

  getDropdown() {
    if (!this.dropdown || !this.contains(this.dropdown)) {
      this.dropdown = this.querySelector('.hp-predictive-results-dropdown');
    }
    if (!this.dropdown && this.form) {
      this.dropdown = document.createElement('div');
      this.dropdown.className = 'hp-predictive-results-dropdown';
      this.form.appendChild(this.dropdown);
    }
    return this.dropdown;
  }

  setupEventListeners() {
    this.input.addEventListener('input', this.onInput.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.form.addEventListener('submit', this.onSubmit.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    const dropdown = this.getDropdown();
    if (dropdown) {
      dropdown.addEventListener('click', this.onDropdownClick.bind(this));
    }

    if (this.clearButton) {
      this.clearButton.addEventListener('click', this.onClearClick.bind(this));
    }

    // Close search dropdown on click outside
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) {
        this.closeDropdown();
      }
    });

    document.addEventListener('touchstart', (e) => {
      if (!this.contains(e.target)) {
        this.closeDropdown();
      }
    }, { passive: true });

    // Close on mobile orientation change or window resize
    window.addEventListener('resize', () => {
      const dropdown = this.getDropdown();
      if (dropdown && dropdown.style.display === 'block') {
        this.handleMobileOverlay(true);
      }
    });
  }

  setupA11y() {
    this.setAttribute('role', 'combobox');
    this.setAttribute('aria-haspopup', 'listbox');
    this.setAttribute('aria-expanded', 'false');
    this.setAttribute('aria-owns', 'predictive-search-results');

    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-controls', 'predictive-search-results');
    this.input.setAttribute('autocomplete', 'off');
  }

  // LRU Caching Helper Methods
  getCache(key) {
    if (!this.cache.has(key)) return null;
    const val = this.cache.get(key);
    this.cache.delete(key);
    this.cache.set(key, val); // Move to end (most recently used)
    return val;
  }

  setCache(key, val) {
    if (this.cache.has(key)) {
      this.cache.delete(key);
    } else if (this.cache.size >= this.cacheLimit) {
      const oldestKey = this.cache.keys().next().value;
      this.cache.delete(oldestKey);
    }
    this.cache.set(key, val);
  }

  // Input events with 250ms debouncing
  onInput() {
    const query = this.input.value.trim();

    if (this.debounceTimer) clearTimeout(this.debounceTimer);

    if (query.length === 0) {
      this.showEmptyState();
      return;
    }

    if (this.clearButton) this.clearButton.style.display = 'block';

    this.debounceTimer = setTimeout(() => {
      this.fetchResults(query);
    }, 250);
  }

  onFocus() {
    const query = this.input.value.trim();
    if (query.length === 0) {
      this.showEmptyState();
    } else {
      this.fetchResults(query);
    }
  }

  onClearClick(e) {
    e.preventDefault();
    this.input.value = '';
    this.closeDropdown();
    if (this.input) this.input.blur();
  }

  onSubmit(e) {
    const query = this.input.value.trim();
    if (query) {
      this.saveRecentSearch(query);
    }
  }

  // Fetch from endpoint /search/suggest
  fetchResults(query) {
    // Check LRU Cache
    const cachedHtml = this.getCache(query);
    if (cachedHtml) {
      this.renderResults(cachedHtml);
      return;
    }

    // Cancel any ongoing fetch request
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    this.showSkeleton();

    const isAr = window.location.pathname.indexOf('/ar') === 0 || document.documentElement.getAttribute('lang') === 'ar';
    const rootPrefix = isAr ? '/ar' : '';
    const baseUrl = window.Shopify?.routes?.predictive_search_url || this.getAttribute('data-url') || (rootPrefix + '/search/suggest');
    const url = `${baseUrl}?q=${encodeURIComponent(query)}&resources[type]=product,collection,query&section_id=predictive-search`;

    fetch(url, { signal })
      .then(response => {
        if (!response.ok) throw new Error('Response error');
        return response.text();
      })
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const results = doc.getElementById('predictive-search-results') || doc.querySelector('.hp-predictive-dropdown-container') || doc.querySelector('.shopify-section') || doc.body;

        if (results && results.innerHTML.trim()) {
          const content = results.outerHTML || results.innerHTML;
          this.setCache(query, content);
          this.renderResults(content);
        } else {
          this.closeDropdown();
        }
      })
      .catch(err => {
        if (err.name !== 'AbortError') {
          console.error('Predictive search fetch failed:', err);
          this.closeDropdown();
        }
      });
  }

  // Empty state displays recent queries from client localStorage if available, or fetches trending searches section
  showEmptyState() {
    if (this.clearButton) this.clearButton.style.display = 'none';

    const recents = this.getRecentSearches();
    const isAr = window.location.pathname.indexOf('/ar') === 0 || document.documentElement.getAttribute('lang') === 'ar';
    const closeLabel = isAr ? 'إغلاق' : 'Close';

    if (recents.length > 0) {
      const recentLabel = this.getAttribute('data-recent-label') || 'Recent Searches';
      let listHtml = '';
      recents.forEach(term => {
        listHtml += `
          <li class="predictive-search__list-item" role="option" aria-selected="false">
            <a href="/search?q=${encodeURIComponent(term)}&type=product" class="predictive-search__item predictive-search__item--query" data-search-term="${term.replace(/"/g, '&quot;')}">
              <svg class="icon icon-history" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              <span>${term}</span>
            </a>
          </li>
        `;
      });

      const html = `
        <div id="predictive-search-results" class="hp-predictive-dropdown-container">
          <div class="predictive-search__group">
            <h3 class="predictive-search__heading">${recentLabel}</h3>
            <ul class="predictive-search__list" role="listbox">
              ${listHtml}
            </ul>
          </div>
        </div>
      `;
      this.renderResults(html);
    } else {
      const rootPrefix = isAr ? '/ar' : '';
      const baseUrl = window.Shopify?.routes?.predictive_search_url || this.getAttribute('data-url') || (rootPrefix + '/search/suggest');
      const url = `${baseUrl}?section_id=predictive-search`;
      fetch(url)
        .then(res => {
          if (!res.ok) throw new Error('Fetch failed');
          return res.text();
        })
        .then(html => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const results = doc.getElementById('predictive-search-results') || doc.querySelector('.hp-predictive-dropdown-container');
          if (results && results.innerHTML.trim()) {
            this.renderResults(results.outerHTML || results.innerHTML);
          } else {
            this.closeDropdown();
          }
        })
        .catch(() => {
          this.closeDropdown();
        });
    }
  }

  // Render & Inject client-side recent terms
  renderResults(html) {
    const dropdown = this.getDropdown();
    if (!dropdown) return;

    dropdown.innerHTML = html;
    dropdown.style.display = 'block';
    dropdown.classList.add('is-active');
    this.setAttribute('aria-expanded', 'true');

    // Handle recent searches placeholder
    const recentPlaceholder = dropdown.querySelector('.predictive-search__recent-placeholder');
    if (recentPlaceholder && recentPlaceholder.getAttribute('data-enable-recent') === 'true') {
      this.injectRecentSearches(recentPlaceholder);
    }

    this.handleMobileOverlay(true);
  }

  closeDropdown() {
    const dropdown = this.getDropdown();
    if (dropdown) {
      dropdown.style.display = 'none';
      dropdown.classList.remove('is-active');
    }
    if (this.clearButton) {
      this.clearButton.style.display = 'none';
    }
    this.setAttribute('aria-expanded', 'false');
    if (this.input) {
      this.input.removeAttribute('aria-activedescendant');
      this.input.blur();
    }
    this.handleMobileOverlay(false);
  }

  // LocalStorage recent searches manager
  getRecentSearches() {
    try {
      return JSON.parse(localStorage.getItem('hp_recent_searches') || '[]');
    } catch(e) {
      return [];
    }
  }

  saveRecentSearch(term) {
    if (!term || term.length < 2) return;
    let searches = this.getRecentSearches();
    searches = searches.filter(s => s.toLowerCase() !== term.toLowerCase());
    searches.unshift(term);
    localStorage.setItem('hp_recent_searches', JSON.stringify(searches.slice(0, 4)));
  }

  injectRecentSearches(placeholder) {
    const recents = this.getRecentSearches();
    if (recents.length === 0) {
      placeholder.style.display = 'none';
      return;
    }

    placeholder.style.display = 'block';
    const recentLabel = this.getAttribute('data-recent-label') || 'Recent Searches';
    const isAr = window.location.pathname.indexOf('/ar') === 0 || document.documentElement.getAttribute('lang') === 'ar';
    const closeLabel = isAr ? 'إغلاق' : 'Close';

    let listHtml = '';
    recents.forEach(term => {
      listHtml += `
        <li class="predictive-search__list-item" role="option" aria-selected="false">
          <a href="/search?q=${encodeURIComponent(term)}" class="predictive-search__item predictive-search__item--query" data-search-term="${term.replace(/"/g, '&quot;')}">
            <svg class="icon icon-history" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
            <span>${term}</span>
          </a>
        </li>
      `;
    });

    placeholder.innerHTML = `
      <div class="predictive-search__group">
        <h3 class="predictive-search__heading">${recentLabel}</h3>
        <ul class="predictive-search__list" role="listbox">
          ${listHtml}
        </ul>
      </div>
    `;
  }

  // Keyboard navigation & accessibility control
  onKeydown(e) {
    const dropdown = this.getDropdown();
    const items = Array.from(this.querySelectorAll('.predictive-search__item'));
    if (items.length === 0 || !dropdown || dropdown.style.display === 'none') {
      if (e.key === 'Escape') {
        this.closeDropdown();
      }
      return;
    }

    let activeIndex = items.findIndex(item => item.getAttribute('aria-selected') === 'true');

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (activeIndex !== -1) {
          items[activeIndex].setAttribute('aria-selected', 'false');
          items[activeIndex].removeAttribute('id');
        }
        activeIndex = (activeIndex + 1) % items.length;
        items[activeIndex].setAttribute('aria-selected', 'true');
        items[activeIndex].id = `predictive-search-option-${activeIndex}`;
        this.input.setAttribute('aria-activedescendant', items[activeIndex].id);
        items[activeIndex].scrollIntoView({ block: 'nearest' });
        break;

      case 'ArrowUp':
        e.preventDefault();
        if (activeIndex !== -1) {
          items[activeIndex].setAttribute('aria-selected', 'false');
          items[activeIndex].removeAttribute('id');
        }
        activeIndex = activeIndex - 1;
        if (activeIndex < 0) activeIndex = items.length - 1;
        items[activeIndex].setAttribute('aria-selected', 'true');
        items[activeIndex].id = `predictive-search-option-${activeIndex}`;
        this.input.setAttribute('aria-activedescendant', items[activeIndex].id);
        items[activeIndex].scrollIntoView({ block: 'nearest' });
        break;

      case 'Escape':
        e.preventDefault();
        this.closeDropdown();
        this.input.focus();
        break;

      case 'Enter':
        if (activeIndex !== -1) {
          e.preventDefault();
          const selectedLink = items[activeIndex];
          const searchTerm = selectedLink.getAttribute('data-search-term');
          if (searchTerm) {
            this.saveRecentSearch(searchTerm);
            this.emitSelectedEvent(searchTerm, selectedLink.href);
          } else {
            const title = selectedLink.querySelector('.predictive-search__product-title, .collection-title-bold')?.textContent || '';
            this.emitSelectedEvent(title, selectedLink.href);
          }
          window.location.href = selectedLink.href;
        } else {
          const query = this.input.value.trim();
          if (query) {
            this.saveRecentSearch(query);
          }
        }
        break;
    }
  }

  // Click handler inside dropdown for analytics tracking & close handling
  onDropdownClick(e) {
    if (e.target.closest('.predictive-search__close-btn')) {
      e.preventDefault();
      this.closeDropdown();
      return;
    }

    const item = e.target.closest('.predictive-search__item');
    if (item) {
      const searchTerm = item.getAttribute('data-search-term');
      if (searchTerm) {
        this.saveRecentSearch(searchTerm);
        this.emitSelectedEvent(searchTerm, item.href);
      } else {
        const title = item.querySelector('.predictive-search__product-title, .collection-title-bold')?.textContent || '';
        this.emitSelectedEvent(title, item.href);
      }
      return;
    }

    if (e.target.closest('.predictive-search__view-all')) {
      return;
    }

    // On mobile viewports, tapping the empty area of the overlay modal closes it
    if (window.innerWidth <= 1024) {
      this.closeDropdown();
    }
  }

  emitSelectedEvent(term, url) {
    const event = new CustomEvent('predictive_search:selected', {
      bubbles: true,
      detail: {
        searchTerm: term.trim(),
        url: url
      }
    });
    this.dispatchEvent(event);
  }

  // Mobile full-height screen overlay scroll-locking
  handleMobileOverlay(isOpen) {
    if (window.innerWidth <= 1024) {
      if (isOpen) {
        document.documentElement.classList.add('predictive-search-locked');
      } else {
        document.documentElement.classList.remove('predictive-search-locked');
      }
    } else {
      document.documentElement.classList.remove('predictive-search-locked');
    }
  }

  // Pulser Skeleton placeholders during fetch requests
  showSkeleton() {
    const dropdown = this.getDropdown();
    if (!dropdown) return;

    dropdown.innerHTML = `
      <div id="predictive-search-results" class="hp-predictive-dropdown-container">
        <div class="predictive-search__skeleton">
          <div class="predictive-search__skeleton-heading"></div>
          <div class="predictive-search__skeleton-list">
            <div class="predictive-search__skeleton-item">
              <div class="predictive-search__skeleton-thumb"></div>
              <div class="predictive-search__skeleton-text-group">
                <div class="predictive-search__skeleton-text-1"></div>
                <div class="predictive-search__skeleton-text-2"></div>
              </div>
            </div>
            <div class="predictive-search__skeleton-item">
              <div class="predictive-search__skeleton-thumb"></div>
              <div class="predictive-search__skeleton-text-group">
                <div class="predictive-search__skeleton-text-1"></div>
                <div class="predictive-search__skeleton-text-2"></div>
              </div>
            </div>
            <div class="predictive-search__skeleton-item">
              <div class="predictive-search__skeleton-thumb"></div>
              <div class="predictive-search__skeleton-text-group">
                <div class="predictive-search__skeleton-text-1"></div>
                <div class="predictive-search__skeleton-text-2"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
    dropdown.style.display = 'block';
    dropdown.classList.add('is-active');
    this.setAttribute('aria-expanded', 'true');
    this.handleMobileOverlay(true);
  }
}

customElements.define('predictive-search', PredictiveSearch);

